import { AuthError, TokenVerificationError, UnknownError } from '../domain/errors/AuthErrors';
import { AuthState, JWTContextType } from '../domain/types/AuthTypes';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import { NavigateFunction } from 'react-router-dom';

import authServices from '../main/DependencyInjection';
import { useUserPreferences } from '@/common/application/hooks/userPreferencesHook';
import { initPendoFromSession } from '@/common/application/utils/pendoSession';
import { clearAllUserStorage } from '@/common/infrastructure/utils/storageCleanup';

const { authService, tokenService } = authServices;

const initialState: AuthState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
};

const AuthContext = createContext<JWTContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactElement }) => {
  const { logoutUser } = useUserPreferences(localStorage.getItem('userName') as string);
  const [state, setState] = useState<AuthState>(initialState);
  const [loading, setLoading] = React.useState(true);
  const [loginValues] = React.useState(authService.getSavedCredentials());

  useEffect(() => {
    const init = async () => {
      try {
        const authToken = tokenService.getToken() ?? '';
        if (!authService.verifyToken(authToken)) {
          logout();
        } else {
          setState((prev) => ({ ...prev, isLoggedIn: true, isInitialized: true }));
          // RCA auth token is a session GUID, not a JWT — identity comes from loginData.
          initPendoFromSession();
        }
      } catch (error) {
        if (error instanceof TokenVerificationError) {
          logout();
        }
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const login = async (UserName: string, password: string, navigate: NavigateFunction) => {
    try {
      await authService.login(
        {
          userName: UserName,
          password,
          rememberMe: false,
        },
        navigate,
      );

      // Initialize Pendo after SPA login (loginData is written during authService.login).
      initPendoFromSession();
    } catch (error) {
      // Re-throw domain errors for UI components to handle
      if (error instanceof AuthError) {
        throw error;
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    activationCode: string,
    username: string,
    phone: string,
  ) => {
    try {
      await authService.register({ email, password, firstName, lastName, activationCode, username, phone });
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  };

  const signup = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    username: string,
    phone: string,
  ) => {
    try {
      await authService.signup({ email, password, firstName, lastName, username, phone });
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  };

  const logout = async () => {
    try {
      // Awaits the server-side logout (records LogoutOn in AppGlobalUserLog)
      // and then clears the local token/header. Server failures are swallowed
      // inside authService.logout() so local cleanup still proceeds.
      await authService.logout();
      logoutUser();

      // Clear all user-related storage data
      clearAllUserStorage();

      // Update auth state
      setState((prev) => ({ ...prev, isLoggedIn: false, isInitialized: true, user: null }));

      console.log('Logout completed successfully');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const resetPassword = async (username: string) => {
    try {
      await authService.resetPassword({ username });
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  };

  const forgotUsername = async (email: string) => {
    try {
      await authService.forgotUsername({ email });
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  };

  const verifyCode = async (username: string, verificationCode: string): Promise<number> => {
    try {
      return await authService.verifyCode({ username, verificationCode });
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  };

  const updatePassword = async (globalUserId: number, password: string, verificationToken?: string) => {
    try {
      await authService.updatePassword({ globalUserId, password, verificationToken });
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  };

  const updateUser = async (
    globalUserId: number,
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
  ) => {
    try {
      await authService.updateUser({ globalUserId, firstName, lastName, email, phone });
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  };

  const updateProfile = () => {};

  const getToken = () => tokenService.getToken();

  const isTokenExpired = () => {
    const token = getToken();
    if (!token) return true;
    return !authService.verifyToken(token);
  };

  const providerValues = useMemo(
    () => ({
      ...state,
      login,
      logout,
      register,
      signup,
      resetPassword,
      forgotUsername,
      verifyCode,
      updatePassword,
      updateUser,
      updateProfile,
      loginValues,
      getToken,
      isTokenExpired,
    }),
    [state, loginValues],
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  return <AuthContext.Provider value={providerValues}>{children}</AuthContext.Provider>;
};

export default AuthContext;
